import type { AxiosRequestConfig } from 'axios';
import { Http } from '@mygpt/app/http';

// 业务性 4xx（409 执行中、400 校验）由页面自己展示，不走全局错误弹窗
const SILENT = { skipErrorNotification: true } as AxiosRequestConfig;

export type KnowledgeSignal = 'ticket' | 'edit_diff' | 'unanswered' | 'negative_feedback';
export type KnowledgePublishMode = 'manual' | 'auto_add' | 'auto_add_update';
export type KnowledgeRunStatus = 'running' | 'done' | 'partial' | 'error' | 'empty';
export type KnowledgeRunTrigger = 'beat' | 'manual' | 'import';
export type KnowledgeUnitStatus =
  | 'candidate'
  | 'approved'
  | 'scheduled'
  | 'rejected'
  | 'merged'
  | 'superseded'
  | 'stale';
export type KnowledgeChangeKind = 'fact_change' | 'wording' | 'questions_added' | 'evidence_added' | 'new';

// 既存との事実の違い（PM 評審 O01）：料金・日付・可否・条件・資格
export interface KnowledgeFactChange {
  item: string;
  old: string;
  new: string;
  basis?: string;
}
export type KnowledgeProposalType = 'add' | 'update' | 'conflict';
export type KnowledgeUnitKind = 'qa' | 'procedure' | 'fact';
export type KnowledgePublishForm = 'faq' | 'document' | 'none';
export type KnowledgeReviewAction =
  | 'approve'
  | 'approve_with_edit'
  | 'reject'
  | 'adopt_new'
  | 'keep_existing'
  | 'withdraw';
export type KnowledgeRejectCode = 'not_generalizable' | 'inaccurate' | 'duplicate' | 'outdated' | 'pii' | 'other';

export interface KnowledgeLoopSettings {
  dataset_id: string;
  enabled: boolean;
  run_hour_jst: number;
  signals: Record<KnowledgeSignal, boolean>;
  publish_mode: KnowledgePublishMode;
  auto_min_confidence: number;
  auto_min_evidence: number;
  max_items_per_run: number;
  daily_token_budget: number;
  // 抽出・統合・判定に使うモデル。null = 環境の既定（llm_model_default）。空文字で送ると既定に戻る
  llm_model?: string | null;
  llm_model_default?: string;
  /** 個人情報の検査（既定 ON）。OFF はメール・電話・マイナンバーだけ遮蔽し、承認時の検査なし */
  pii_check_enabled?: boolean;
  last_run_at?: string | null;
  last_run_id?: string | null;
}

export interface KnowledgeRunStats {
  pii_masked?: number;
  inputs?: Partial<Record<KnowledgeSignal, number>>;
  collected?: Partial<Record<KnowledgeSignal, number>>;
  items_total?: number;
  items_done?: number;
  candidates?: number;
  clusters?: number;
  clusters_done?: number;
  add?: number;
  update?: number;
  noop?: number;
  conflict?: number;
  auto_published?: number;
  skipped?: number;
  llm_failed?: number;
  demand_linked?: number;
  memories_used?: number;
  memory_hints?: number;
  tokens?: number;
  deferred?: number;
  budget_exhausted?: boolean;
  duration_sec?: number;
  phase?: 'import' | 'collect' | 'extract' | 'consolidate' | 'memory' | 'done';
  memory?: { patterns?: number; glossary?: number; topics?: number; decayed?: number; archived?: number };
  skip_samples?: { source_ref: string; reason: string }[];
  // 需要（答えられなかった質問）の行き先：既存に紐付け / 候補に紐付け / 未解決
  demand?: Partial<Record<KnowledgeGapResolution, number>>;
  // FAQ 画面で直接編集された公開稿を台帳に取り込んだ件数
  faq_synced?: number;
  // 同じ目標への重複提案（証拠だけ寄せた）/ 品質ゲートで作廃した候補
  duplicate_target?: number;
  quality_rejected?: number;
  // 第 2 輪：言い直し / AI 稿そのまま送信の工単 / 同じ答えに寄せた束 / 表現だけの更新 / 矛盾→競合
  retried?: number;
  as_is_skipped?: number;
  same_as_merged?: number;
  wording_noop?: number;
  contradiction_conflict?: number;
  // 統合稿が候補の数値を落として統合し直した束
  merge_retried?: number;
}

export interface KnowledgeRun {
  id: string;
  trigger: KnowledgeRunTrigger;
  status: KnowledgeRunStatus;
  started_at?: string | null;
  finished_at?: string | null;
  stats: KnowledgeRunStats;
  error?: string | null;
}

export interface KnowledgeRunDetail extends KnowledgeRun {
  units: KnowledgeUnitSummary[];
}

export interface KnowledgeLoopStatus {
  enabled: boolean;
  run_hour_jst: number;
  publish_mode: KnowledgePublishMode;
  next_run_at?: string | null;
  running?: KnowledgeRun | null;
  last_run?: KnowledgeRun | null;
  pending_items: number;
  source_items: number;
  queue: { total: number; add: number; update: number; conflict: number };
  approved_units: number;
  scheduled_units?: number;
  open_gaps?: number;
}

export interface KnowledgeUnitSummary {
  id: string;
  kind: KnowledgeUnitKind;
  title: string;
  body: string;
  similar_questions: string[];
  conditions: Record<string, string>;
  status: KnowledgeUnitStatus;
  proposal_type: KnowledgeProposalType;
  target_unit_id?: string | null;
  confidence?: number | null;
  evidence_count: number;
  publish_form: KnowledgePublishForm;
  publish_target_id?: string | null;
  version: number;
  signals: KnowledgeSignal[];
  has_uncertainties: boolean;
  has_contradictions?: boolean;
  pii_masked?: Record<string, number> | null;
  pii_flagged?: Record<string, number> | null;
  fact_changes?: KnowledgeFactChange[];
  effective_from?: string | null;
  change_kind?: KnowledgeChangeKind | null;
  draft_revision?: number;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  reject_code?: string | null;
  reject_note?: string | null;
  created_by?: string | null;
  run_id?: string | null;
  last_verified_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface KnowledgeRationale {
  why_extracted?: string;
  generalizability?: string;
  evidence_quotes?: { source_item_id?: string | null; source_ref?: string | null; quote: string }[];
  merge_decision?: string;
  merge_reason?: string;
  change_summary?: string;
  form_decision?: string;
  form_reason?: string;
  memories_applied?: { memory_id: string; kind: string; text: string }[];
  uncertainties?: string[];
  contradictions?: string[];
  fact_changes?: KnowledgeFactChange[];
  effective_from?: string | null;
  change_kind?: KnowledgeChangeKind | null;
  evidence_basis?: 'agent_reply' | string;
  edit_classes?: string[];
  signals?: KnowledgeSignal[];
  backfilled?: boolean;
  /** 抽出時に自動遮蔽した個人情報（型別件数） */
  pii_masked?: Record<string, number>;
  /** 既存データの再検査で公開中の本文に見つかった個人情報（型別件数、本文は未変更） */
  pii_flagged?: Record<string, number>;
}

export interface KnowledgeEvidence {
  id: string;
  kind: KnowledgeSignal | string;
  source_ref?: string | null;
  quote?: string | null;
  source_item_id?: string | null;
  content?: string | null;
  subject?: string | null;
  url?: string | null;
  created_at?: string | null;
}

export interface KnowledgeRevision {
  version?: number | null; // 正式版本（公開済みの稿）
  revision_no?: number | null; // 候補段階の修訂回数
  title: string;
  body: string;
  change_summary?: string | null;
  source: string;
  author?: string | null;
  created_at?: string | null;
}

// この知識（の FAQ）を実際に引用したドラフトの成績（直近 N 日）
export interface KnowledgeUnitUsage {
  drafts: number;
  as_is: number;
  edited: number;
  self: number;
  days: number;
  recent: {
    ticket_id: number;
    draft_version: number;
    status: string;
    similarity?: number | null;
    edit_reason?: string | null;
    answered_at?: string | null;
    created_at?: string | null;
    unit_version?: number | null;
  }[];
}

export interface KnowledgeUnitDetail extends KnowledgeUnitSummary {
  rationale: KnowledgeRationale;
  evidence: KnowledgeEvidence[];
  revisions: KnowledgeRevision[];
  usage?: KnowledgeUnitUsage;
  target?: {
    type: 'unit' | 'faq';
    id: string;
    title: string;
    body: string;
    version?: number | null;
    status: string;
  } | null;
}

export interface KnowledgeUnitPatch {
  title?: string;
  body?: string;
  similar_questions?: string[];
  kind?: KnowledgeUnitKind;
  publish_form?: KnowledgePublishForm;
}

export interface KnowledgeUnitListParams {
  status?: string;
  proposal_type?: KnowledgeProposalType;
  kind?: KnowledgeUnitKind;
  publish_form?: KnowledgePublishForm;
  q?: string;
  sort?: 'newest' | 'confidence' | 'evidence';
  limit?: number;
  offset?: number;
}

export const getKnowledgeLoopSettings = (datasetId: string) => {
  const { get } = Http.use();
  return get<KnowledgeLoopSettings>(`/datasets/${datasetId}/knowledge-loop/settings`);
};

export const putKnowledgeLoopSettings = (datasetId: string, body: Partial<KnowledgeLoopSettings>) => {
  const { put } = Http.use();
  return put<KnowledgeLoopSettings>(`/datasets/${datasetId}/knowledge-loop/settings`, body, SILENT);
};

export const getKnowledgeLoopStatus = (datasetId: string) => {
  const { get } = Http.use();
  return get<KnowledgeLoopStatus>(`/datasets/${datasetId}/knowledge-loop/status`);
};

export const listKnowledgeLoopRuns = (datasetId: string, params: { limit?: number; offset?: number } = {}) => {
  const { get } = Http.use();
  return get<{ total: number; items: KnowledgeRun[] }>(`/datasets/${datasetId}/knowledge-loop/runs`, { params });
};

export const getKnowledgeLoopRun = (datasetId: string, runId: string) => {
  const { get } = Http.use();
  return get<KnowledgeRunDetail>(`/datasets/${datasetId}/knowledge-loop/runs/${runId}`);
};

export const triggerKnowledgeLoopRun = (datasetId: string, importTickets = false) => {
  const { post } = Http.use();
  return post<{ queued: boolean; run: KnowledgeRun }>(`/datasets/${datasetId}/knowledge-loop/run`, null, {
    ...SILENT,
    params: { import_tickets: importTickets },
  });
};

// 承認時の重複警告（409）の中身
export interface KnowledgeDuplicateDetail {
  code: 'duplicate';
  unit_id: string;
  title: string;
  status: KnowledgeUnitStatus;
  score: number;
}

/** この回をやり直す：この回が作った未審査の候補を消し、原料を未処理に戻して再抽出 */
export const redoKnowledgeLoopRun = (datasetId: string, runId: string) => {
  const { post } = Http.use();
  return post<{ deleted_units: number; reset_items: number; run: KnowledgeRun }>(
    `/datasets/${datasetId}/knowledge-loop/runs/${runId}/redo`,
    null,
    SILENT
  );
};

export interface KnowledgePiiSection {
  scanned: number;
  with_pii: number;
  counts: Record<string, number>;
}

export interface KnowledgePiiBackfillReport {
  apply: boolean;
  source_items: KnowledgePiiSection;
  evidence: KnowledgePiiSection;
  candidates: KnowledgePiiSection;
  published: KnowledgePiiSection & {
    units: { id: string; title: string; status: string; fields: Record<string, Record<string, number>> }[];
  };
  memories: KnowledgePiiSection;
}

/** 既存データの個人情報を再検査。apply=false は報告だけ、true で再遮蔽（公開中は印だけ） */
export const runPiiBackfill = (datasetId: string, apply: boolean) => {
  const { post } = Http.use();
  return post<KnowledgePiiBackfillReport>(`/datasets/${datasetId}/knowledge-loop/pii-backfill`, { apply });
};

export const backfillKnowledgeUnits = (datasetId: string) => {
  const { post } = Http.use();
  return post<{ scanned: number; created: number; approved: number; candidate: number; rejected: number }>(
    `/datasets/${datasetId}/knowledge-loop/backfill`
  );
};

export const listKnowledgeUnits = (datasetId: string, params: KnowledgeUnitListParams = {}) => {
  const { get } = Http.use();
  return get<{ total: number; items: KnowledgeUnitSummary[] }>(`/datasets/${datasetId}/knowledge-units`, { params });
};

/** FAQ 画面から台帳へ：この FAQ を投影している単元（404 = 台帳にない手動 FAQ） */
export const getKnowledgeUnitByFaq = (datasetId: string, faqId: string) => {
  const { get } = Http.use();
  return get<KnowledgeUnitSummary>(`/datasets/${datasetId}/knowledge-units/by-faq/${faqId}`, SILENT);
};

export const getKnowledgeUnit = (datasetId: string, unitId: string) => {
  const { get } = Http.use();
  return get<KnowledgeUnitDetail>(`/datasets/${datasetId}/knowledge-units/${unitId}`);
};

export const patchKnowledgeUnit = (datasetId: string, unitId: string, body: KnowledgeUnitPatch) => {
  const { patch } = Http.use();
  return patch<KnowledgeUnitDetail>(`/datasets/${datasetId}/knowledge-units/${unitId}`, body, SILENT);
};

export const reviewKnowledgeUnit = (
  datasetId: string,
  unitId: string,
  body: {
    action: KnowledgeReviewAction;
    patch?: KnowledgeUnitPatch;
    reject_code?: KnowledgeRejectCode;
    reject_note?: string;
    effective_from?: string; // YYYY-MM-DD（JST）。未来なら公開予定
    force?: boolean; // 公開中 / 生効待ちとの重複警告（409 duplicate）を押し切る
  }
) => {
  const { post } = Http.use();
  return post<{ unit: KnowledgeUnitSummary; published_unit_id: string; publish_target_id?: string | null }>(
    `/datasets/${datasetId}/knowledge-units/${unitId}/review`,
    body,
    SILENT
  );
};

// ============ 需要（ナレッジ不足）キュー ============

export type KnowledgeGapResolution =
  | 'gap_open'
  | 'linked_unit'
  | 'linked_candidate'
  | 'excluded'
  | 'covered'
  | 'linked_file';

export interface KnowledgeGapItem {
  id: string;
  question: string;
  signal_type: KnowledgeSignal | string;
  source_type: string;
  source_ref: string;
  subject?: string | null;
  skip_detail?: string | null;
  asked_at?: string | null;
  url?: string | null;
  resolution: KnowledgeGapResolution;
  resolution_ref?: string | null;
  resolution_note?: string | null;
  assignee?: string | null;
  gap_cluster_key?: string | null;
  last_seen_at?: string | null;
}

/** 似た質問の束：件数・最終出現・担当・紐づいた知識 */
export interface KnowledgeGapGroup {
  cluster_key: string;
  items: KnowledgeGapItem[];
  count: number;
  assignee?: string | null;
  sample_question: string;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  linked_unit_id?: string | null;
  linked_unit?: { id: string; title: string; status: KnowledgeUnitStatus } | null;
  /** 再確認（covered）/ 手動のドキュメント紐付け（linked_file）の先 */
  linked_ref?: { kind: 'file' | 'faq'; id: string; name: string } | null;
}

export interface KnowledgeGapRecheckResult {
  answerable: boolean;
  reason: string;
  answer: string;
  hits: { kind: 'file' | 'faq'; id: string; name: string; score?: number | null }[];
  linked: number;
  items: KnowledgeGapItem[];
}

export const listKnowledgeGaps = (
  datasetId: string,
  params: { status?: 'open' | 'linked' | 'excluded' | 'all'; limit?: number; offset?: number } = {}
) => {
  const { get } = Http.use();
  return get<{ total: number; items: KnowledgeGapGroup[] }>(`/datasets/${datasetId}/knowledge-loop/gaps`, {
    params,
  });
};

export const patchKnowledgeGap = (
  datasetId: string,
  itemId: string,
  body: {
    assignee?: string;
    resolution?: 'gap_open' | 'excluded';
    resolution_ref?: string;
    note?: string;
    apply_to_cluster?: boolean;
  }
) => {
  const { patch } = Http.use();
  return patch<{ items: KnowledgeGapItem[] }>(`/datasets/${datasetId}/knowledge-loop/gaps/${itemId}`, body, SILENT);
};

/** 人が回答を書いて候補にする。束の他の質問も同じ候補に紐づく */
/** 再確認：ナレッジベース（FAQ ＋ ドキュメント）で今なら答えられるかを当場で判定。答えられれば束が covered になる */
export const recheckKnowledgeGap = (datasetId: string, itemId: string) => {
  const { post } = Http.use();
  return post<KnowledgeGapRecheckResult>(
    `/datasets/${datasetId}/knowledge-loop/gaps/${itemId}/recheck`,
    { apply_to_cluster: true },
    SILENT
  );
};

export const answerKnowledgeGap = (
  datasetId: string,
  itemId: string,
  body: { title: string; body: string; similar_questions?: string[]; apply_to_cluster?: boolean }
) => {
  const { post } = Http.use();
  return post<{ unit: KnowledgeUnitSummary; linked: number }>(
    `/datasets/${datasetId}/knowledge-loop/gaps/${itemId}/answer`,
    body,
    SILENT
  );
};

// ============ 記憶（第二步） ============

export type KnowledgeMemoryKind =
  | 'rejection_pattern'
  | 'style_example'
  | 'edit_pattern'
  | 'glossary'
  | 'topic_map'
  | 'reviewer_instruction';
export type KnowledgeMemoryStatus = 'active' | 'proposed' | 'archived';

export interface KnowledgeMemory {
  id: string;
  kind: KnowledgeMemoryKind;
  text: string;
  content?: Record<string, unknown> | null;
  strength: number;
  hits: number;
  status: KnowledgeMemoryStatus;
  origin: 'auto' | 'manual' | 'chat';
  pinned: boolean;
  evidence?: Record<string, unknown>[] | null;
  source_unit_id?: string | null;
  source_unit?: { id: string; title: string; status: KnowledgeUnitStatus; proposal_type: KnowledgeProposalType } | null;
  created_by?: string | null;
  last_used_at?: string | null;
  last_reinforced_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export const listKnowledgeMemories = (datasetId: string, params: { status?: string; kind?: string } = {}) => {
  const { get } = Http.use();
  return get<{ items: KnowledgeMemory[]; counts: Record<KnowledgeMemoryStatus, number> }>(
    `/datasets/${datasetId}/knowledge-memories`,
    { params }
  );
};

export const createKnowledgeMemory = (datasetId: string, body: { kind: KnowledgeMemoryKind; text: string }) => {
  const { post } = Http.use();
  return post<KnowledgeMemory>(`/datasets/${datasetId}/knowledge-memories`, body, SILENT);
};

export const patchKnowledgeMemory = (
  datasetId: string,
  memoryId: string,
  body: { kind?: KnowledgeMemoryKind; text?: string; status?: 'active' | 'archived' }
) => {
  const { patch } = Http.use();
  return patch<KnowledgeMemory>(`/datasets/${datasetId}/knowledge-memories/${memoryId}`, body, SILENT);
};

// ============ 審査ドロワーの AI 対話（第二步） ============

export interface KnowledgePatchProposal {
  // 審査者が手直しして適用した
  edited_by_reviewer?: boolean;
  fields: KnowledgeUnitPatch;
  summary?: string;
  unsupported?: string[];
}

export interface KnowledgeMemoryProposal {
  kind: KnowledgeMemoryKind;
  text: string;
}

export interface KnowledgeReviewMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  proposed_patch?: KnowledgePatchProposal | null;
  patch_applied: boolean;
  memory_proposal?: KnowledgeMemoryProposal | null;
  memory_saved: boolean;
  author?: string | null;
  created_at?: string | null;
}

export const listKnowledgeReviewMessages = (datasetId: string, unitId: string) => {
  const { get } = Http.use();
  return get<{ items: KnowledgeReviewMessage[] }>(`/datasets/${datasetId}/knowledge-units/${unitId}/messages`);
};

// fields：審査者がカード上で手直しした項目（提案に無い項目は無視される）。省略で AI の提案どおり
export const applyKnowledgeReviewPatch = (
  datasetId: string,
  unitId: string,
  messageId: string,
  fields?: { title?: string; body?: string; similar_questions?: string[] }
) => {
  const { post } = Http.use();
  return post<{ unit: KnowledgeUnitDetail; message: KnowledgeReviewMessage }>(
    `/datasets/${datasetId}/knowledge-units/${unitId}/messages/${messageId}/apply-patch`,
    fields ? { fields } : null,
    SILENT
  );
};

export const saveKnowledgeReviewMemory = (datasetId: string, unitId: string, messageId: string, text?: string) => {
  const { post } = Http.use();
  return post<{ memory: KnowledgeMemory; message: KnowledgeReviewMessage }>(
    `/datasets/${datasetId}/knowledge-units/${unitId}/messages/${messageId}/save-memory`,
    { text },
    SILENT
  );
};

export interface KnowledgeReviewChatHandlers {
  onDelta: (text: string) => void;
  onLookup?: (step: { tool: string; query: string }) => void;
  onDone: (message: KnowledgeReviewMessage) => void;
  onError: (detail?: string) => void;
}

/** POST の SSE を fetch で読む（lookup / delta / proposal / done / error）。中断は signal で */
export const streamKnowledgeReviewChat = async (
  datasetId: string,
  unitId: string,
  message: string,
  handlers: KnowledgeReviewChatHandlers,
  signal?: AbortSignal
) => {
  let finished = false;
  const dispatch = (event: string, raw: string) => {
    let data: any = null;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (event === 'delta') handlers.onDelta(data.text || '');
    else if (event === 'lookup') handlers.onLookup?.(data);
    else if (event === 'done') {
      finished = true;
      handlers.onDone(data);
    } else if (event === 'error') {
      finished = true;
      handlers.onError(data?.detail);
    }
  };
  try {
    const response = await Http.use().fetch(`/datasets/${datasetId}/knowledge-units/${unitId}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ message }),
      signal,
    });
    const reader = (response as Response)?.body?.getReader?.();
    if (!reader) {
      handlers.onError();
      return;
    }
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      // sse_starlette は CRLF で区切る。LF に揃えてから空行でイベントを切り出す
      // （CRLF がチャンクの境目で割れても拾えるよう、連結してから置換する）
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');
      let sep = buffer.indexOf('\n\n');
      while (sep >= 0) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        let event = 'message';
        const dataLines: string[] = [];
        block.split('\n').forEach((line) => {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
        });
        if (dataLines.length) dispatch(event, dataLines.join('\n'));
        sep = buffer.indexOf('\n\n');
      }
    }
    if (!finished) handlers.onError();
  } catch (e: any) {
    if (e?.name !== 'AbortError' && !finished) handlers.onError();
  }
};
